/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

import { SLRSpinner, isEmptyOrNull } from '@slr/shared-library';
import { APP_URL, INVITATION_ALREADY_MEMBER_ERROR, INVITATION_HANDLE } from 'configs';
import { useCheckInvitation } from 'hooks';
import { useQueryParam } from 'providers';
import { FC, useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { parseUserServiceUniqueExceptionType } from 'utils/parseUserServiceError';
import { AlreadyMember, Invalid, InvitationModal } from '.';

import './Invitation.scss';

export const InvitationCheckPage: FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'invitationsPage'
  });

  const { invitationQuery } = useQueryParam();
  const { token = '', organizationId = '' } = invitationQuery || {};

  const {
    isPending: isCheckLoading,
    isError: isCheckError,
    error: checkError
  } = useCheckInvitation(token, organizationId);

  const [isAlreadyMemberError, setIsAlreadyMemberError] = useState(false);

  useEffect(() => {
    if (!isCheckError || !checkError) {
      setIsAlreadyMemberError(false);
      return;
    }

    void parseUserServiceUniqueExceptionType(checkError).then(
      (uniqueExceptionType) => {
        setIsAlreadyMemberError(
          uniqueExceptionType === INVITATION_ALREADY_MEMBER_ERROR
        );
      }
    );
  }, [checkError, isCheckError]);

  const hasMissingParams = useMemo(
    () => isEmptyOrNull(token) || isEmptyOrNull(organizationId),
    [organizationId, token]
  );

  const isInvalid = useMemo(
    () => (isCheckError && !isAlreadyMemberError) || hasMissingParams,
    [hasMissingParams, isAlreadyMemberError, isCheckError]
  );

  useEffect(() => {
    if (!isCheckLoading && !isCheckError && !isInvalid) {
      window.location.replace(
        `${APP_URL}${INVITATION_HANDLE}?organizationId=${organizationId}&token=${token}`
      );
    }
  }, [isCheckError, isCheckLoading, isInvalid, organizationId, token]);

  return (
    <Container className="invitation" fluid>
      {isCheckLoading && <SLRSpinner variant="light" />}
      {isAlreadyMemberError && (
        <InvitationModal title={t('alreadyMember.title')}>
          <AlreadyMember />
        </InvitationModal>
      )}
      {isInvalid && (
        <InvitationModal title={t('invalid.title')}>
          <Invalid />
        </InvitationModal>
      )}
    </Container>
  );
};
